import { useRef, useState, useCallback, useEffect } from "react";
import { resampleTo16k } from "../utils/audioResampler";
import { encodeWav } from "../utils/wavEncoder";
import { loadModel, runVad, resetVadState } from "../utils/onnxHelpers";
import type * as ort from "onnxruntime-web";

export type AudioFlagType =
  | "background_noise"
  | "background_speech"
  | "multiple_speakers";

export interface AudioFlagPayload {
  type: AudioFlagType;
  rms: number;
  timestamp: number;
  message?: string;
}

export type WarningLevel = "none" | "low" | "medium" | "high";

export interface AudioStatus {
  currentVolume: number;
  smoothedRms: number;
  baselineRms: number;
  calibrationProgress: number;
  isCalibrated: boolean;
  voiceDetected: boolean;
  speakerCountEstimate: number;
  flags: string[];
  warningLevel: WarningLevel;
  warningMessage: string | null;
  timeSinceLastSpeech: number;
  isListening: boolean;
  permissionGranted: boolean;
}

const computeRms = (samples: Float32Array): number => {
  let sum = 0;
  for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
  return Math.sqrt(sum / samples.length);
};

export interface VoiceProctoringConfig {
  user_id: string;
  user_exam_id: string;
  assessment_id: string;
  baselineRms?: number;
  baselineDb?: number;
  baselineAudioUrl?: string;
  onFlag?: (payload: AudioFlagPayload) => void;
}
const VAD_MODEL_PATH = "/models/silero_vad.onnx";

const AUDIO_VIOLATION_ENDPOINT =
  "https://8vlzvdalb2.execute-api.eu-west-1.amazonaws.com/prod/audio-wav-verify";

const RING_BUFFER_SECONDS = 30;
const FRAME_SAMPLE_RATE = 16000;
const RING_MAX_FRAMES = RING_BUFFER_SECONDS * FRAME_SAMPLE_RATE;

const SP_BUFFER_SIZE = 4096;

const VAD_FRAME_SIZE = 512;

const VAD_EVERY_N_FRAMES = 3;

const STATE_UPDATE_INTERVAL_MS = 100;

const MIN_SPEECH_BUFFER_MS = 1500;

const MAX_RECENT_EMBEDDINGS = 5;

const MAX_UPLOAD_QUEUE = 3;

const COOLDOWNS: Record<string, number> = {
  background_noise: 30_000,
  unknown_voice: 20_000,
  multiple_speakers: 30_000,
};

const VAD_SMOOTH_WINDOW = 5;
const NOISE_EMA_ALPHA = 0.05;

interface UploadEntry {
  type: AudioFlagType;
  wav: Blob;
  segmentNumber: number;
}

export function useVoiceProctoring(config: VoiceProctoringConfig = {} as VoiceProctoringConfig) {
  const {
    user_id = "",
    user_exam_id = "",
    assessment_id = "",
    baselineRms = 0.05,
    baselineAudioUrl = "",
    onFlag,
  } = config;

  const [isListening, setIsListening] = useState(false);
  const [currentRms, setCurrentRms] = useState(0);
  const [audioStatus, setAudioStatus] = useState<AudioStatus>({
    currentVolume: 0,
    smoothedRms: 0,
    baselineRms: baselineRms,
    calibrationProgress: 0,
    isCalibrated: false,
    voiceDetected: false,
    speakerCountEstimate: 0,
    flags: [],
    warningLevel: "none",
    warningMessage: null,
    timeSinceLastSpeech: 0,
    isListening: false,
    permissionGranted: false,
  });

  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const isListeningRef = useRef(false);
  const isVoiceTriggeredRef = useRef(false);

  const ringPcmRef = useRef<Float32Array[]>([]);
  const ringTsRef = useRef<number[]>([]);

  const speechBufRef = useRef<Float32Array[]>([]);
  const speechStartTsRef = useRef<number>(0);

  // VAD state
  const vadProbsRef = useRef<number[]>([]);
  const frameCountRef = useRef(0);
  const inSpeechRef = useRef(false);

  // Speaker embeddings
  const recentEmbeddingsRef = useRef<Float32Array[]>([]);
  const baselineEmbeddingRef = useRef<Float32Array | null>(null);

  // Adaptive noise floor (EMA)
  const adaptiveFloorRef = useRef(baselineRms);

  // Noise violation accumulator
  const noiseAccRef = useRef(0);
  const lastNoiseCheckTsRef = useRef(0);

  // Cooldown stamps
  const lastFiredRef = useRef<Record<string, number>>({});

  // Violation segment counter
  const segmentNumberRef = useRef(0);

  // Upload queue
  const uploadQueueRef = useRef<UploadEntry[]>([]);
  const uploadInProgressRef = useRef(false);

  // ML model references (null = not yet loaded or failed)
  const vadSessionRef = useRef<ort.InferenceSession | null>(null);
  const ecapaSessionRef = useRef<ort.InferenceSession | null>(null);
  const mlAvailable = useRef(false);

  // Calibration
  const isCalibrated = useRef(false);
  const calibrationProgressRef = useRef(0);

  // Status update throttle
  const lastStatusUpdateRef = useRef(0);
  const statusCacheRef = useRef<AudioStatus>({
    currentVolume: 0,
    smoothedRms: 0,
    baselineRms: baselineRms,
    calibrationProgress: 0,
    isCalibrated: false,
    voiceDetected: false,
    speakerCountEstimate: 0,
    flags: [],
    warningLevel: "none",
    warningMessage: null,
    timeSinceLastSpeech: 0,
    isListening: false,
    permissionGranted: false,
  });

  const lastSpeechTsRef = useRef(Date.now());

  const onFlagRef = useRef(onFlag);
  useEffect(() => { onFlagRef.current = onFlag; }, [onFlag]);

  const configRef = useRef({ user_id, user_exam_id, assessment_id });
  useEffect(() => {
    configRef.current = { user_id, user_exam_id, assessment_id };
  }, [user_id, user_exam_id, assessment_id]);

  const fireFlag = useCallback(
    (type: AudioFlagType, rms: number, message?: string) => {
      const now = Date.now();
      const cooldown = COOLDOWNS[type] ?? 10_000;
      if (now - (lastFiredRef.current[type] ?? 0) < cooldown) return;
      lastFiredRef.current[type] = now;
      //  console.log(`[VoiceProctoring] 🚨 Violation: ${type}`);
      if (onFlagRef.current) {
        onFlagRef.current({ type, rms, timestamp: now, message });
      }
    },
    []
  );

  const extractClip = (seconds: number): Float32Array => {
    const nowTs = Date.now();
    const cutoff = nowTs - seconds * 1000;
    const frames: Float32Array[] = [];

    const tss = ringTsRef.current;
    const pcm = ringPcmRef.current;

    for (let i = 0; i < tss.length; i++) {
      if (tss[i] >= cutoff) frames.push(pcm[i]);
    }

    if (frames.length === 0) return new Float32Array(0);

    const total = frames.reduce((s, f) => s + f.length, 0);
    const out = new Float32Array(total);
    let offset = 0;
    for (const f of frames) {
      out.set(f, offset);
      offset += f.length;
    }
    return out;
  };

  const processUploadQueue = useCallback(async () => {
    if (uploadInProgressRef.current) return;
    if (uploadQueueRef.current.length === 0) return;

    const entry = uploadQueueRef.current.shift()!;
    uploadInProgressRef.current = true;

    try {
      const form = new FormData();
      const { user_id, user_exam_id, assessment_id } = configRef.current;
      form.append("user_id", user_id);
      form.append("user_exam_id", user_exam_id);
      form.append("assessment_id", assessment_id);
      form.append("segment_number", String(entry.segmentNumber));
      form.append("violation_type", entry.type);
      form.append("audio_segment", entry.wav, `segment_${entry.segmentNumber}.wav`);

      const response = await fetch(AUDIO_VIOLATION_ENDPOINT, { method: "POST", body: form });
      const data = await response.json();
      // console.log(`[VoiceProctoring] 🚀 Server response for clip #${entry.segmentNumber}:`, data);
    } catch (err) {
      //console.warn("[VoiceProctoring] Upload failed (non-fatal):", err);
    } finally {
      uploadInProgressRef.current = false;
      if (uploadQueueRef.current.length > 0) {
        processUploadQueue();
      }
    }
  }, []);

  // ── Send violation clip 
  const sendViolationClip = useCallback(
    async (type: AudioFlagType) => {
      if (uploadQueueRef.current.length >= MAX_UPLOAD_QUEUE) {
        uploadQueueRef.current.shift();
      }

      const clip = extractClip(10); // last 10 seconds
      if (clip.length === 0) return;

      const wav = encodeWav(clip, FRAME_SAMPLE_RATE);
      const segNum = ++segmentNumberRef.current;

      console.log(`[VoiceProctoring] 📣 Triggering upload for ${type} (Clip #${segNum})`);
      uploadQueueRef.current.push({ type, wav, segmentNumber: segNum });
      processUploadQueue();
    },
    [processUploadQueue]
  );

  // ── Run ECAPA on speech buffer and check violations ──────────────────────
  const analyzeSpeechSegment = useCallback(async () => {
    if (speechBufRef.current.length === 0) return;

    speechBufRef.current = [];

    const rms = 0.1;
    sendViolationClip("multiple_speakers");
  }, [fireFlag, sendViolationClip]);

  const processFrame = useCallback(
    async (rawFrame: Float32Array, nativeSampleRate: number) => {
      const frame16k = await resampleTo16k(rawFrame, nativeSampleRate);
      const nowTs = Date.now();

      ringPcmRef.current.push(frame16k);
      ringTsRef.current.push(nowTs);

      const Ring = ringPcmRef.current;
      let totalRingSamples = Ring.reduce((s, f) => s + f.length, 0);
      while (totalRingSamples > RING_MAX_FRAMES && Ring.length > 0) {
        const removed = Ring.shift()!;
        ringTsRef.current.shift();
        totalRingSamples -= removed.length;
      }

      const rms = computeRms(frame16k);
      setCurrentRms(rms);

      frameCountRef.current++;
      let speechProb = inSpeechRef.current ? 0.6 : 0.4;

      if (
        frameCountRef.current % VAD_EVERY_N_FRAMES === 0 &&
        vadSessionRef.current
      ) {
        try {
          const vadInput = new Float32Array(VAD_FRAME_SIZE);
          vadInput.set(frame16k.slice(0, VAD_FRAME_SIZE));
          speechProb = await runVad(vadSessionRef.current, vadInput);
        } catch {
          speechProb = rms > 0.02 ? 0.7 : 0.3;
        }

        vadProbsRef.current.push(speechProb);
        if (vadProbsRef.current.length > VAD_SMOOTH_WINDOW) {
          vadProbsRef.current.shift();
        }
      }

      const smoothedProb =
        vadProbsRef.current.length > 0
          ? vadProbsRef.current.reduce((a, b) => a + b, 0) /
          vadProbsRef.current.length
          : 0.5;

      if (frameCountRef.current % 15 === 0) {
        //  const peak = rawFrame.reduce((a, b) => Math.max(a, Math.abs(b)), 0);
        // const status = (smoothedProb > 0.4) ? "🗣️ SPEECH" : (rms > 0.02) ? "🎵 NOISE" : "🤫 QUIET";
        // console.log(`[VoiceProctoring Debug] Status: ${status} | RMS: ${rms.toFixed(4)} | Peak: ${peak.toFixed(4)} | VAD: ${smoothedProb.toFixed(3)}`);
      }

      const wasInSpeech = inSpeechRef.current;
      if (!wasInSpeech && smoothedProb > 0.4) { // Lowered from 0.5
        inSpeechRef.current = true;
        speechStartTsRef.current = nowTs;
        speechBufRef.current = [];
      } else if (wasInSpeech && smoothedProb < 0.2) { // Lowered from 0.35
        inSpeechRef.current = false;
        const durationMs = nowTs - speechStartTsRef.current;
        if (durationMs >= MIN_SPEECH_BUFFER_MS) {
          analyzeSpeechSegment();
        } else {
          speechBufRef.current = [];
        }
      }

      if (inSpeechRef.current) {
        lastSpeechTsRef.current = nowTs;
        speechBufRef.current.push(frame16k);
      }

      if (!inSpeechRef.current) {
        adaptiveFloorRef.current =
          NOISE_EMA_ALPHA * rms +
          (1 - NOISE_EMA_ALPHA) * adaptiveFloorRef.current;
      }

      const isLoud = rms > 0.025;
      const isVoice = smoothedProb > 0.45;

      if ((isLoud || isVoice) && !isVoiceTriggeredRef.current) {
        const reason = isVoice ? "Voice Detected" : "Noise Detected";
        // console.log(`[VoiceProctoring] 🚨 Violation Triggered! Reason: ${reason}`);
        isVoiceTriggeredRef.current = true;
        if (isVoice) {
          sendViolationClip("multiple_speakers");
        } else {
          fireFlag("background_noise", rms, reason);
          sendViolationClip("background_noise");
        }

        setTimeout(() => {
          isVoiceTriggeredRef.current = false;
        }, 15000);
      }
      lastNoiseCheckTsRef.current = nowTs;

      if (nowTs - lastStatusUpdateRef.current >= STATE_UPDATE_INTERVAL_MS) {
        lastStatusUpdateRef.current = nowTs;
        const flags: string[] = [];
        if (noiseAccRef.current > 0) flags.push("background_noise");

        const updated: AudioStatus = {
          currentVolume: Math.min(100, rms * 200),
          smoothedRms: rms,
          baselineRms: baselineRms,
          calibrationProgress: calibrationProgressRef.current,
          isCalibrated: isCalibrated.current,
          voiceDetected: inSpeechRef.current,
          speakerCountEstimate: statusCacheRef.current.speakerCountEstimate,
          flags,
          warningLevel: "none",
          warningMessage: null,
          timeSinceLastSpeech: nowTs - lastSpeechTsRef.current,
          isListening: true,
          permissionGranted: true,
        };

        statusCacheRef.current = updated;
        setAudioStatus(updated);
      }
    },
    [baselineRms, fireFlag, sendViolationClip, analyzeSpeechSegment]
  );

  const initialise = useCallback(async () => {
    try {
      vadSessionRef.current = await loadModel(VAD_MODEL_PATH);
      calibrationProgressRef.current = 0.5;
    } catch (err) {
      console.warn("[VoiceProctoring] VAD load failed - falling back to RMS only.");
    }

    mlAvailable.current = vadSessionRef.current !== null;

    calibrationProgressRef.current = 1.0;
    isCalibrated.current = true;
    setAudioStatus((prev) => ({ ...prev, calibrationProgress: 1.0, isCalibrated: true }));
  }, []);

  const startListening = useCallback(async () => {
    if (isListeningRef.current) return;
    isListeningRef.current = true;
    resetVadState();
    ringPcmRef.current = [];
    ringTsRef.current = [];
    speechBufRef.current = [];
    vadProbsRef.current = [];
    frameCountRef.current = 0;
    inSpeechRef.current = false;
    recentEmbeddingsRef.current = [];
    baselineEmbeddingRef.current = null;
    lastFiredRef.current = {};
    segmentNumberRef.current = 0;
    uploadQueueRef.current = [];
    uploadInProgressRef.current = false;
    adaptiveFloorRef.current = 0.05;
    noiseAccRef.current = 0;
    isCalibrated.current = false;
    calibrationProgressRef.current = 0;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
    } catch (err) {
      setAudioStatus((prev) => ({ ...prev, permissionGranted: false, isListening: false }));
      setIsListening(false);
      isListeningRef.current = false;
      return;
    }

    streamRef.current = stream;
    const audioCtx = new AudioContext();
    audioContextRef.current = audioCtx;
    const source = audioCtx.createMediaStreamSource(stream);
    sourceRef.current = source;

    // @ts-ignore
    const processor = audioCtx.createScriptProcessor(SP_BUFFER_SIZE, 1, 1);
    processorRef.current = processor;

    let lastLogTs = 0;
    processor.onaudioprocess = (event: AudioProcessingEvent) => {
      if (!isListeningRef.current) return;

      const inputData = event.inputBuffer.getChannelData(0);
      const copy = new Float32Array(inputData);
      const sampleRate = audioCtx.sampleRate;

      processFrame(copy, sampleRate);

      const now = Date.now();
      if (now - lastLogTs > 2000) {
        // console.log(`[VoiceProctoring] 🎤 Mic Active: Receiving data at ${sampleRate}Hz`);
        lastLogTs = now;
      }
    };

    source.connect(processor);
    processor.connect(audioCtx.destination);

    // console.log(`[VoiceProctoring] ✅ Audio pipeline connected. SampleRate: ${audioCtx.sampleRate}Hz, Threshold: ${(baselineRms * 3.0).toFixed(4)}`);
    setIsListening(true);
    isListeningRef.current = true;
    setAudioStatus((prev) => ({ ...prev, isListening: true, permissionGranted: true }));
    initialise();
  }, [initialise, processFrame, baselineRms]);

  const stopListening = useCallback(() => {
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current.onaudioprocess = null;
      processorRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => { });
      audioContextRef.current = null;
    }

    setIsListening(false);
    isListeningRef.current = false;
    setCurrentRms(0);
    setAudioStatus((prev) => ({ ...prev, isListening: false, voiceDetected: false, currentVolume: 0 }));
  }, []);

  useEffect(() => {
    return () => {
      stopListening();
    };
  }, [stopListening]);

  return {
    startListening,
    stopListening,
    isListening,
    currentRms,
    audioStatus,
  };
}
